// Por onde vai a "Desistir no portal", decidido pelo portalOrigem do item.
//
// Até 05/10/2026 só o BLL ia pelo caminho gkz; o BNC (mesma plataforma, base gkz)
// caía no else do ComprasNet. O backend barra antes de qualquer chamada externa
// (withdrawCompra → assertComprasNetItem → 400), então NÃO havia risco de desistir
// no portal errado — medição do Bastião, por leitura. O que havia era um botão que
// SEMPRE falhava para BNC, com mensagem escrita para desenvolvedor. A rota certa
// já existia: POST /procurement/proposals/:id/withdraw-bnc.

export type RotaDaDesistencia =
  | { tipo: 'gkz'; portal: 'BLL' | 'BNC'; rotulo: string }
  | { tipo: 'licitanet'; rotulo: string }
  | { tipo: 'comprasnet'; rotulo: string }
  | { tipo: 'indisponivel'; motivo: string };

export function rotaDaDesistencia(portalOrigem: string | null | undefined): RotaDaDesistencia {
  const po = String(portalOrigem || '').toLowerCase();
  if (po === 'pcp') {
    return { tipo: 'indisponivel', motivo: 'Desistência no Portal Compras Públicas ainda não implementada — faça pelo portal' };
  }
  if (po === 'bll') return { tipo: 'gkz', portal: 'BLL', rotulo: 'BLL Compras' };
  if (po === 'bnc') return { tipo: 'gkz', portal: 'BNC', rotulo: 'BNC Compras' };
  // Até 19/10/2026 o Licitanet caía no ComprasNet e o backend recusava (assertComprasNetItem): o botão SEMPRE falhava.
  if (po === 'licitanet') return { tipo: 'licitanet', rotulo: 'Licitanet' };
  return { tipo: 'comprasnet', rotulo: 'Compras.gov.br (cnetmobile)' };
}

/** Dispara a desistência gkz no endpoint do portal certo; devolve o jobId. */
export function pedirDesistenciaGkz(
  portal: 'BLL' | 'BNC',
  servico: {
    withdrawProposalBll: (id: number) => Promise<{ jobId: number }>;
    withdrawProposalBnc: (id: number) => Promise<{ jobId: number }>;
  },
  proposalId: number,
): Promise<{ jobId: number }> {
  return portal === 'BNC' ? servico.withdrawProposalBnc(proposalId) : servico.withdrawProposalBll(proposalId);
}

/**
 * DESISTIR NO LICITANET (backend `POST /procurement/proposals/:id/withdraw-licitanet`, Bastião, 19/10/2026).
 *
 * Síncrono: o backend lê do portal, na hora, o `codProposta` de cada item e apaga um por um (DELETE), parando no
 * primeiro que o portal recusar. Apaga a proposta INTEIRA; dá para cadastrar e enviar de novo enquanto o prazo estiver
 * aberto, mas não para desfazer. Exige `confirmarDesistencia: true`.
 * - 200 `ok: true`: tudo apagado; proposta e item viram `desistida`;
 * - 200 `ok: false`, `parcial: false`: o portal recusou o primeiro, nada saiu;
 * - 200 `ok: false`, `parcial: true`: ⛔ apagou parte e parou. A proposta ficou mutilada no portal e NÃO foi marcada
 *   como desistida: quem lê precisa saber quantos saíram, quantos ficaram, e terminar na tela do portal;
 * - 400 `{codigo}` ANTES de apagar qualquer coisa (PORTAL_NAO_RESPONDEU, PROPOSTA_NAO_EXISTE_NO_PORTAL, CONFIRMACAO_EXIGIDA).
 * - só ADMIN (403 do HUB para os outros perfis).
 * Contrato FINAL da #518 (22/10/2026): sucesso só com 204 em todos os DELETE; leitura vazia do portal é falha (400
 * PORTAL_NAO_RESPONDEU); a frase do backend chega à tela como está.
 */
export interface RemocaoNoLicitanet { codProposta: number; ok: boolean; status: number; mensagem: string }
export interface ResultadoDaDesistenciaLicitanet {
  ok: boolean;
  parcial?: boolean;
  mensagem?: string;
  itensRemovidos?: number[];
  itensMantidos?: number[];
  remocoes?: RemocaoNoLicitanet[];
  statusDaProposta?: string;
}

/** O que a confirmação diz antes de desistir no Licitanet. */
export const CONSEQUENCIA_DA_DESISTENCIA_LICITANET =
  'Apaga a proposta INTEIRA no portal (todos os itens), não só a marca de desistida no HUB. Não há desfazer: enquanto o '
  + 'prazo de envio estiver aberto, ela pode ser cadastrada e enviada de novo pelo HUB; depois do fim do prazo, não.';

const semPontoFinal = (t: string) => t.trim().replace(/[.\s]+$/, '');

/** A frase do PORTAL na remoção que falhou, com o HTTP (só quando o backend não mandou a dele). */
const doPortal = (r: RemocaoNoLicitanet | undefined) =>
  r ? `O portal respondeu (HTTP ${r.status}): ${semPontoFinal(r.mensagem || 'sem mensagem')}.` : '';

/** A frase do BACKEND, como ela veio (contrato final da #518, 22/10/2026: a recusa chega à tela como está). */
const doBackend = (t: unknown): string | null => (typeof t === 'string' && t.trim() ? t.trim() : null);

/**
 * Quando não dá para saber o que aconteceu no portal. ⛔ Não é "nada foi apagado": os DELETE podem ter saído antes de a
 * resposta se perder, e dizer que nada saiu levaria alguém a desistir de novo sobre uma proposta já mutilada.
 */
export const DESFECHO_DESCONHECIDO =
  'Não sei o que aconteceu no portal: a resposta da desistência não chegou inteira, e os registros podem ter sido '
  + 'apagados em parte. Confira a proposta na tela da Licitanet antes de tentar de novo.';

/** O resultado, para ler: título, frase (a do backend, quando há) e o tom. */
export function resultadoDaDesistenciaLicitanet(r: ResultadoDaDesistenciaLicitanet | null | undefined): { tom: 'sucesso' | 'falha' | 'parcial' | 'desconhecido'; titulo: string; frase: string } {
  // ⛔ Sucesso é `ok === true` e mais nada (o backend só dá `ok` com 204 em TODAS as remoções). Um corpo sem `ok`
  // booleano é resposta que não se lê: chave ausente não é `false`.
  if (!r || typeof r !== 'object' || typeof r.ok !== 'boolean') {
    return { tom: 'desconhecido', titulo: 'Resultado da desistência desconhecido', frase: DESFECHO_DESCONHECIDO };
  }
  const removidos = r.itensRemovidos?.length ?? 0;
  const mantidos = r.itensMantidos?.length ?? 0;
  const falhou = (r.remocoes || []).find((x) => !x.ok);
  if (r.ok === true) {
    return {
      tom: 'sucesso',
      titulo: 'Proposta apagada do portal',
      frase: `A proposta foi apagada da Licitanet (${removidos} ${removidos === 1 ? 'registro removido' : 'registros removidos'}) e marcada como desistida no HUB. Enquanto o prazo estiver aberto, dá para cadastrá-la e enviá-la de novo.`,
    };
  }
  if (r.parcial) {
    return {
      tom: 'parcial',
      titulo: 'Desistência pela metade',
      frase: doBackend(r.mensagem)
        ?? `O portal apagou ${removidos} ${removidos === 1 ? 'registro' : 'registros'} e parou: ${mantidos} ${mantidos === 1 ? 'continua' : 'continuam'} na Licitanet. `
          + 'A proposta ficou incompleta no portal e NÃO foi marcada como desistida. Termine na tela da Licitanet. '
          + doPortal(falhou),
    };
  }
  return {
    tom: 'falha',
    titulo: 'O portal recusou a desistência',
    frase: doBackend(r.mensagem) ?? `Nada foi apagado: a proposta continua no portal e enviada no HUB. ${doPortal(falhou)}`.trim(),
  };
}

/**
 * As recusas (400 do POST, ANTES de apagar: PORTAL_NAO_RESPONDEU, CONFIRMACAO_EXIGIDA, PROPOSTA_NAO_EXISTE_NO_PORTAL).
 * ⛔ A frase do backend vai COMO ESTÁ: é nela que vem o HTTP da leitura e, num 401/403, a dica de sessão e de permissão
 * (a permissão do DELETE no portal não foi medida; 403 é a primeira hipótese). Parafrasear apagava justamente isso.
 */
export function fraseDaRecusaDaDesistenciaLicitanet(e: any): string {
  const resposta = e?.response;
  // Sem resposta (rede, timeout do nginx): o POST pode ter apagado no portal antes de a resposta se perder.
  if (!resposta) return DESFECHO_DESCONHECIDO;
  const d = resposta.data;
  if (resposta.status === 400) {
    const frase = doBackend(d?.mensagem) ?? doBackend(d?.message) ?? `o HUB recusou sem dizer o motivo (código ${d?.codigo ?? 'ausente'})`;
    return /nada foi apagado/i.test(frase) ? frase : `Nada foi apagado. ${frase}`;
  }
  if (resposta.status === 403) return 'Nada foi apagado. O HUB só deixa um admin desistir no Licitanet.';
  // 5xx (o 504 do nginx corta em 60s e o job segue no servidor): também não se sabe o que saiu.
  if (resposta.status >= 500) return DESFECHO_DESCONHECIDO;
  return `Nada foi apagado. ${doBackend(d?.mensagem) ?? doBackend(d?.message) ?? `o HUB respondeu HTTP ${resposta.status}`}`;
}
